import React from 'react'
import { Box, Typography, Divider } from '@mui/material'
import WarningIcon from '@meprism/shared/src/assets/icons/Warning2.svg'
import InfoIcon from '@meprism/shared/src/assets/icons/info2.svg'
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type ActivityGraphProps = {
  labels: string[];
  found: number[];
  removed: number[];
  inProgress: number[];
};

const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom" as const,
      align: "start" as const,
      labels: {
        pointStyle: "rect",
        boxWidth: 15,
        boxHeight: 5,
        padding: 40,
        usePointStyle: false
      },
    },
    title: {
      display: false,
    },
  },
};


const ActivityGraph: React.FC<ActivityGraphProps> = ({ labels, found, removed, inProgress }) => {
  const chartData = {
    labels,
    datasets: [
      {
        label: "Records Found",
        data: found,
        borderColor: "#1E4DB7",
        backgroundColor: "#1E4DB7",
        tension: 0,
      },
      {
        label: "Records Removed",
        data: removed,
        borderColor: "#8B4DB7",
        backgroundColor: "#8B4DB7",
        tension: 0,
      },
      {
        label: "In Progress",
        data: inProgress,
        borderColor: "#E54E7E",
        backgroundColor: "#E54E7E",
        tension: 0,
      },
    ],
  };


  return (<>
    <Box 
      sx={{ 
        width: '100%',
        my: '20px',
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontWeight: 600,
          fontStyle: 'Semi Bold',
          fontSize: '20px',
          lineHeight: '100%',
          letterSpacing: '0%',
          mb: 2,
          opacity: 1,
        }}
      >
        Privacy Activity Over Time
      </Typography>
    </Box>

    {/* Graph container - now responsive */}
    <Box
      sx={{
        width: '100%',
        maxWidth: '1065px',
        minWidth: '300px',
        height: 'auto',
        backgroundColor: '#fff',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
        opacity: 1,
        pt: '38px !important',
        pb: 2,
        borderRadius: '7px',
        pr: '45px',
      }}
    >
      <Box
        sx={{
          height: 'auto',
          minHeight: '40px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '16px', md: '78px' },
          opacity: 1,
          mx: 'auto',
          justifyContent: 'space-between',
          ml: '30px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '683px',
            minWidth: '200px',
            height: 'auto',
            minHeight: '44px',
            display: 'flex',
            gap: '9px',
            alignItems: 'center',
            opacity: 1,
            px: 2,
            py: 1,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: '44px',
              height: '44px',
              minWidth: '44px',
              borderRadius: '22px',
              backgroundColor: '#64109C',
              opacity: 1,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: '22px',
                height: '22px',
                position: 'absolute',
                top: '10px',
                left: '11px',
              }}
            >
              <img src={WarningIcon} alt="icon" />
            </Box>
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: '120%',
              letterSpacing: '0%',
              color: '#555',
              flex: 1,
              wordWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            Track how many records were found, removed, and are still in progress each month.
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: '218px',
            minWidth: '180px',
            height: 'auto',
            minHeight: '24px',
            display: "flex",
            gap: "8px",
            justifyContent: { xs: 'flex-start', md: 'center' },
            alignItems: "center",
            my: { xs: 0, md: "8px" },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "24px",
              height: "24px",
              minWidth: '24px',
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <img src={InfoIcon} alt="icon" style={{ width: "100%", height: "100%" }} />
          </Box>
          <Box
            sx={{
              width: '100%',
              maxWidth: '186px',
              height: 'auto',
              opacity: 1,
              flex: 1,
          }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: { xs: '12px', sm: '13.5px' },
                lineHeight: "120%",
                letterSpacing: "0em",
                color: "#808080",
                wordWrap: 'break-word',
              }}
            >
              (Click & drag chart to zoom)
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider 
        sx={{ 
          mt: "35px",
          mb: "35px",
          borderColor: "#ECECEC",
          borderWidth: "0 0 2px 0",
          height: "2px",
        }}
      />

      {/* Graph placeholder */}
      <Box
        sx={{
          width: "101%",
          minHeight: { xs: "200px", sm: "350px", md: "300px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#aaa",
          fontSize: "14px",
          pl: 1,
        }}
      >
        <Line
          data={chartData}
          options={{ ...options, maintainAspectRatio: false }}
          style={{ width: "100%", height: "100%", marginBottom: '16px' }}
        />
      </Box>
    </Box>
    <Box
      sx={{
        height: "30px",
        mt: 2,
      }}
    ></Box>
  </>)
}

export default ActivityGraph
